'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Typography } from '@royalaholddelhaize/design-system-pantry-web/foundation/typography/typography';
import { SupportedLocale } from '../../types';
import { createTestHook } from '../../utils/create-test-hook';
import { Star } from './elements/star';
import { I18nProvider } from './i18n/i18n-provider';
import css from './rating.module.scss';

export enum RatingVariant {
    EASE_OF_USE = 'EASE_OF_USE',
    SATISFACTION = 'SATISFACTION',
    QUALITY = 'QUALITY',
}

interface RatingProps {
    onChange: (rating: number | undefined) => void;
    value: number | undefined;
    hasError: boolean;
    variant: RatingVariant;
    disabled?: boolean;
    unselectEnabled?: boolean;
    locale: SupportedLocale;
}

const getTPrefix = (variant: RatingVariant): string => {
    switch (variant) {
        case RatingVariant.SATISFACTION:
            return 'satisfaction';
        case RatingVariant.EASE_OF_USE:
            return 'ease-of-use';
        case RatingVariant.QUALITY:
            return 'quality';
        default:
            return 'satisfaction';
    }
};

export const RatingComponent: React.FC<RatingProps> = ({
    onChange,
    value,
    hasError,
    variant,
    disabled = false,
    unselectEnabled = true,
}) => {
    const { t } = useTranslation('rating');
    const [hover, setHover] = React.useState<number | undefined>();

    const tPrefix = getTPrefix(variant);
    const MAX_RATING = 5;
    const ratings = Array.from({ length: MAX_RATING }, (_, i) => i + 1);

    const handleRating = (rating: number) => {
        if (unselectEnabled && rating === value) {
            return onChange(undefined);
        }

        return onChange(rating);
    };

    const ratingCaptionNumber = hover || value;

    return (
        <>
            <div
                className={classNames(css.root, {
                    [css.isRated]: !!value,
                    [css.disabled]: disabled,
                })}
                {...createTestHook('rating')}
                onMouseLeave={() => {
                    setHover(undefined);
                }}
            >
                <div role="region">
                    {ratings.map((rating, index) => {
                        const ratingLabel = t(`${tPrefix}.rating.${rating}`);
                        const selected = value === rating;
                        const ariaLabel = selected
                            ? t('star-aria-label-selected', {
                                  position: index + 1,
                                  rating: ratingLabel,
                              })
                            : t('star-aria-label', {
                                  position: index + 1,
                                  rating: ratingLabel,
                              });

                        return (
                            <span
                                key={rating}
                                className={classNames(css.star, {
                                    [css.selected]:
                                        hover === undefined && selected,
                                })}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    handleRating(rating);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleRating(rating);
                                    }
                                }}
                                onMouseOver={() => {
                                    setHover(rating);
                                }}
                                onFocus={() => {}}
                                aria-label={ariaLabel}
                                {...createTestHook('rating-star')}
                            >
                                <Star />
                            </span>
                        );
                    })}

                    {!hasError && (value || hover) && (
                        <Typography
                            variant="subtext-regular"
                            className={css.caption}
                        >
                            {t(`${tPrefix}.rating.${ratingCaptionNumber}`)}
                        </Typography>
                    )}
                </div>
            </div>
        </>
    );
};

export const Rating: React.FC<RatingProps> = props => {
    return (
        <I18nProvider locale={props.locale}>
            <RatingComponent {...props} />
        </I18nProvider>
    );
};
